import { lazy } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router-dom'
import { routes } from '@/app/routes'
import { RequireAuth, RequireGuest, RequireIncompleteProfile, RequireRole, RootRedirect } from '@/app/guards'
import { AppShell } from '@/layouts/AppShell'
import { RouteErrorPage } from '@/components/shared/ErrorPage'

// Pages are code-split per route group so the first paint only ships the shell.
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const VerifyPage = lazy(() => import('@/pages/auth/VerifyPage'))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'))
const PlansRedirect = lazy(() => import('@/pages/landlord/PlansRedirect'))
const CompleteProfilePage = lazy(() => import('@/pages/auth/CompleteProfilePage'))
const EmailVerifiedPage = lazy(() => import('@/pages/auth/EmailVerifiedPage'))
const PayPage = lazy(() => import('@/pages/payments/PayPage'))
const LandlordDashboardPage = lazy(() => import('@/pages/landlord/DashboardPage'))
const PropertiesPage = lazy(() => import('@/pages/landlord/PropertiesPage'))
const PropertyDetailPage = lazy(() => import('@/pages/landlord/property/PropertyDetailPage'))
const PropertyFormPage = lazy(() => import('@/pages/landlord/property/PropertyFormPage'))
const LeasesPage = lazy(() => import('@/pages/landlord/LeasesPage'))
const TenantsPage = lazy(() => import('@/pages/landlord/TenantsPage'))
const LeaseCreatePage = lazy(() => import('@/pages/landlord/lease/LeaseCreatePage'))
const TransactionsPage = lazy(() => import('@/pages/landlord/TransactionsPage'))
const SubscriptionsPage = lazy(() => import('@/pages/landlord/SubscriptionsPage'))
const TenantDashboardPage = lazy(() => import('@/pages/tenant/DashboardPage'))
const LeaseDetailPage = lazy(() => import('@/pages/lease/LeaseDetailPage'))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))
const SettingsPage = lazy(() => import('@/pages/account/SettingsPage'))
const AccountEditPage = lazy(() => import('@/pages/account/AccountEditPage'))
const NotificationPrefsPage = lazy(() => import('@/pages/account/NotificationPrefsPage'))
const KycPage = lazy(() => import('@/pages/account/KycPage'))
const SupportPage = lazy(() => import('@/pages/account/SupportPage'))
const SupportRequestPage = lazy(() => import('@/pages/account/SupportRequestPage'))
const PreferencesPage = lazy(() => import('@/pages/account/PreferencesPage'))
const SessionsPage = lazy(() => import('@/pages/account/SessionsPage'))
const ReferPage = lazy(() => import('@/pages/account/ReferPage'))
const AccountLayout = lazy(() => import('@/pages/account/AccountLayout'))
const TenantProfilePage = lazy(() => import('@/pages/tenant/TenantProfilePage'))
const MyInterestsPage = lazy(() => import('@/pages/tenant/interests/MyInterestsPage'))
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const PaymentAddPage = lazy(() => import('@/pages/payments/PaymentAddPage'))
const MaintenancePage = lazy(() => import('@/pages/landlord/maintenance/MaintenancePage'))
const BrowsePage = lazy(() => import('@/pages/tenant/browse/BrowsePage'))
const BrowseDetailPage = lazy(() => import('@/pages/tenant/browse/BrowseDetailPage'))
const AgreementPage = lazy(() => import('@/pages/agreement/AgreementPage'))
const AgreementDocumentPage = lazy(() => import('@/pages/agreement/AgreementDocumentPage'))
const AgreementCreatePage = lazy(() => import('@/pages/agreement/AgreementCreatePage'))
const InterestsPage = lazy(() => import('@/pages/landlord/interest/InterestsPage'))
const LeaseHistoryPage = lazy(() => import('@/pages/lease/history/LeaseHistoryPage'))
const GalleryPage = lazy(() => import('@/pages/gallery/GalleryPage'))

export const routeObjects: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/', element: <RootRedirect /> },

      // Each page draws its own AuthFrame.
      {
        element: <RequireGuest />,
        children: [
          { path: routes.login, element: <LoginPage /> },
          { path: routes.verify, element: <VerifyPage /> },
        ],
      },

      { path: routes.signup, element: <SignupPage /> },
      { path: routes.plans, element: <PlansRedirect /> },
      { path: routes.emailVerified, element: <EmailVerifiedPage /> },
      { path: routes.pay, element: <PayPage /> },

      {
        element: <RequireIncompleteProfile />,
        children: [{ path: routes.completeProfile, element: <CompleteProfilePage /> }],
      },

      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              {
                element: <RequireRole role="landlord" />,
                children: [
                  { path: routes.landlord.root, element: <RootRedirect /> },
                  { path: routes.landlord.dashboard, element: <LandlordDashboardPage /> },
                  { path: routes.landlord.properties, element: <PropertiesPage /> },
                  { path: routes.landlord.propertyInterests, element: <InterestsPage /> },
                  { path: '/landlord/properties/:id', element: <PropertyDetailPage /> },
                  { path: routes.landlord.leases, element: <LeasesPage /> },
                  { path: routes.landlord.tenants, element: <TenantsPage /> },
                  { path: routes.landlord.leaseHistory, element: <LeaseHistoryPage /> },
                  { path: routes.landlord.transactions, element: <TransactionsPage /> },
                  { path: routes.landlord.paymentNew, element: <PaymentAddPage /> },
                  { path: routes.landlord.subscriptions, element: <SubscriptionsPage /> },
                  { path: routes.landlord.maintenance, element: <MaintenancePage /> },
                ],
              },
              {
                element: <RequireRole role="tenant" />,
                children: [
                  { path: routes.tenant.root, element: <RootRedirect /> },
                  { path: routes.tenant.dashboard, element: <TenantDashboardPage /> },
                  { path: routes.tenant.transactions, element: <TransactionsPage /> },
                  { path: routes.tenant.browse, element: <BrowsePage /> },
                  { path: '/tenant/browse/:id', element: <BrowseDetailPage /> },
                  { path: routes.tenant.profile, element: <TenantProfilePage /> },
                  { path: routes.tenant.leaseHistory, element: <LeaseHistoryPage /> },
                  { path: routes.tenant.interests, element: <MyInterestsPage /> },
                ],
              },
              { path: '/lease/:id', element: <LeaseDetailPage /> },
              { path: '/lease/:id/agreement', element: <AgreementPage /> },
              { path: '/lease/:id/agreement/document', element: <AgreementDocumentPage /> },
              { path: '/lease/:id/pay', element: <PaymentAddPage /> },
              { path: routes.notifications, element: <NotificationsPage /> },
              {
                element: <AccountLayout />,
                children: [
                  { path: routes.account, element: <SettingsPage /> },
                  { path: routes.accountEdit, element: <AccountEditPage /> },
                  { path: routes.accountNotifications, element: <NotificationPrefsPage /> },
                  { path: routes.kyc, element: <KycPage /> },
                  { path: routes.support, element: <SupportPage /> },
                  { path: '/account/support/:id', element: <SupportRequestPage /> },
                  { path: routes.accountPreferences, element: <PreferencesPage /> },
                  { path: routes.accountSessions, element: <SessionsPage /> },
                  { path: routes.accountRefer, element: <ReferPage /> },
                ],
              },
            ],
          },
          // Full-screen tasks (wizards) sit outside the shell, as DsOverlay.fullScreen does on mobile.
          {
            element: <RequireRole role="landlord" />,
            children: [
              { path: routes.landlord.propertyNew, element: <PropertyFormPage /> },
              { path: '/landlord/properties/:id/edit', element: <PropertyFormPage /> },
              { path: routes.landlord.leaseNew, element: <LeaseCreatePage /> },
              { path: '/lease/:id/agreement/new', element: <AgreementCreatePage /> },
              { path: '/lease/:id/agreement/edit', element: <AgreementCreatePage /> },
            ],
          },
        ],
      },

      // Component gallery: public, not in nav.
      { path: routes.gallery, element: <GalleryPage /> },
      { path: routes.forbidden, element: <ForbiddenPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export const createAppRouter = () => createBrowserRouter(routeObjects)
